/*
 * Landing Page Tracker
 * ------------------------------------------------------------
 * Stamps cart note attributes from the page where an item was
 * last added (not from a prior landing visit alone).
 *
 * Shopify Flow can still read "Landing Page" / "Affiliate" on
 * order_created. Values update on every successful /cart/add.js.
 *
 * When the cart becomes empty (clear, or last line removed), all
 * cart attributes are cleared so sticky quiz / gift / freegifts /
 * landing data cannot outlive the cart. The BOGOS `sca_fg_codes`
 * cookie is also pruned to only the current page's freegifts_code
 * (or cleared entirely). Same prune runs on homepage visits.
 * ------------------------------------------------------------
 */
(function () {
  var STORAGE_KEY = 'landing_attribution';
  var CART_MUTATION_URL_PATTERN = /\/cart\/(change|update|add|clear)\.js/;
  var watcherInstalled = false;
  var clearingAttributes = false;
  var stampingLanding = false;
  var skipInlineStamp = false;

  function getUtmParams() {
    var params = new URLSearchParams(window.location.search);
    var utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function (key) {
      var val = params.get(key);
      if (val) utm[key] = val;
    });
    return utm;
  }

  function isAffiliateUrl() {
    return window.location.pathname.indexOf('-aff') !== -1;
  }

  function pathHandleFallback() {
    var path = (window.location.pathname || '').replace(/\/+$/, '');
    var parts = path.split('/').filter(Boolean);
    if (!parts.length) return 'home';
    if (parts[0] === 'products' && parts[1]) return 'product:' + parts[1];
    if (parts[0] === 'pages' && parts[1]) return parts[1];
    return parts[parts.length - 1] || 'unknown';
  }

  function resolveLandingPageId() {
    var body = document.body;
    if (!body) return pathHandleFallback();

    var suffix = (body.getAttribute('data-template-suffix') || '').trim();
    if (suffix) return suffix;

    var productHandle = (body.getAttribute('data-product-handle') || '').trim();
    if (productHandle) return 'product:' + productHandle;

    var pageHandle = (body.getAttribute('data-page-handle') || '').trim();
    if (pageHandle) return pageHandle;

    return pathHandleFallback();
  }

  function buildAttributionRecord(landingPageId) {
    return {
      handle: landingPageId,
      url: window.location.href,
      referrer: document.referrer || '',
      utm: getUtmParams(),
      timestamp: new Date().toISOString(),
      affiliate: isAffiliateUrl()
    };
  }

  function writeStored(record) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {}
  }

  function currentLandingAttributes() {
    var record = buildAttributionRecord(resolveLandingPageId());
    writeStored(record);
    if (!record.handle) return null;
    var attributes = {
      'Landing Page': record.handle,
      'Landing URL': record.url,
      'Landing Referrer': record.referrer || '',
      'Landing Visited At': record.timestamp,
      Affiliate: record.affiliate ? 'true' : ''
    };
    Object.keys(record.utm || {}).forEach(function (key) {
      attributes['Landing ' + key] = record.utm[key];
    });
    return attributes;
  }

  // mgn-special-shop sends these on its own post-add /cart/update.js.
  // skipInlineStamp stops this watcher from firing a second update for that add.
  window.AmbrosiaLandingTracker = {
    beginInlineStamp: function () {
      skipInlineStamp = true;
      return currentLandingAttributes() || {};
    },
    abortInlineStamp: function () {
      skipInlineStamp = false;
    },
    stampNow: function () {
      return stampLandingFromCurrentPage();
    }
  };

  function syncLandingAttributes() {
    if (stampingLanding) return Promise.resolve();
    var attributes = currentLandingAttributes();
    if (!attributes) return Promise.resolve();
    stampingLanding = true;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes })
    })
      .catch(function () {})
      .finally(function () {
        stampingLanding = false;
      });
  }

  function stampLandingFromCurrentPage() {
    return syncLandingAttributes();
  }

  function pruneBogosFgCookie() {
    if (!window.AmbrosiaBogoUrlParams || typeof window.AmbrosiaBogoUrlParams.pruneScaFgCodesToCurrentLanding !== 'function') {
      return;
    }
    window.AmbrosiaBogoUrlParams.pruneScaFgCodesToCurrentLanding();
  }

  function clearAllCartAttributes() {
    if (clearingAttributes) return Promise.resolve();
    clearingAttributes = true;

    return getCart()
      .then(function (cart) {
        // Cart may have been refilled (clear → add). Never wipe attrs then.
        if (!cart || typeof cart.item_count !== 'number' || cart.item_count > 0) {
          return;
        }
        pruneBogosFgCookie();
        var existing = cart.attributes || {};
        var keys = Object.keys(existing).filter(function (key) {
          var value = existing[key];
          return value != null && String(value) !== '';
        });
        if (!keys.length) return;

        var cleared = {};
        keys.forEach(function (key) {
          cleared[key] = '';
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: cleared })
        });
      })
      .catch(function () {})
      .finally(function () {
        clearingAttributes = false;
      });
  }

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res || !res.ok) throw new Error('cart read failed');
      return res.json();
    });
  }

  function afterCartMutation(url) {
    if (/\/cart\/add\.js/i.test(url) && skipInlineStamp) {
      skipInlineStamp = false;
      return;
    }
    if (clearingAttributes || stampingLanding) return;

    if (/\/cart\/add\.js/i.test(url)) {
      stampLandingFromCurrentPage();
      return;
    }

    // clear / change / update: wipe attrs only if the cart is still empty
    clearAllCartAttributes();
  }

  function installCartWatcher() {
    if (watcherInstalled) return;
    watcherInstalled = true;

    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartMutation = CART_MUTATION_URL_PATTERN.test(url);
      var result = originalFetch.apply(window, arguments);
      if (isCartMutation) {
        result
          .then(function (response) {
            if (response && response.ok) afterCartMutation(String(url));
            return response;
          })
          .catch(function () {});
      }
      return result;
    };
  }

  function rememberVisitOnly() {
    // Keep a browser-side breadcrumb for debugging, but do not write the cart
    // until an item is actually added from this (or a later) page.
    var landingPageId = resolveLandingPageId();
    if (!landingPageId) return;
    writeStored(buildAttributionRecord(landingPageId));
  }

  function init() {
    installCartWatcher();
    rememberVisitOnly();

    // Homepage: drop leftover BOGOS campaign codes (keep URL code only if present).
    if (window.AmbrosiaBogoUrlParams && window.AmbrosiaBogoUrlParams.isHomepage()) {
      pruneBogosFgCookie();
    }

    // If the shopper already has an empty cart with leftover attrs (e.g. after
    // a prior clear.js that did not wipe attributes), clean up once on load.
    getCart()
      .then(function (cart) {
        if (cart && cart.item_count === 0) return clearAllCartAttributes();
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
